import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MdArrowBackIosNew, MdArrowForwardIos } from "react-icons/md";
import defaultAvatar from "../../assets/profile.png";
import { liveStreamPath, useLiveSellers } from "./LiveShopColumn";

const formatPrice = (value, symbol = "$") => `${symbol}${Number(value || 0).toFixed(2)}`;

// "Watch Now & Shop Live": sellers live right now, each with the featured product of the live
export default function ProductCarousel() {
  const navigate = useNavigate();
  const streams = useLiveSellers();
  const [current, setCurrent] = useState(0);
  // Each slide carries a fixed 160px product card, so show fewer slides on narrow screens.
  const getItemsToShow = () =>
    window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 2 : 3;
  const [itemsToShow, setItemsToShow] = useState(getItemsToShow);

  useEffect(() => {
    const onResize = () => setItemsToShow(getItemsToShow());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const lives = streams || [];
  const canSlide = lives.length > itemsToShow;
  // The list is refreshed in the background, so the position may point past a live that has ended
  const start = lives.length ? current % lives.length : 0;
  const visibleLives = canSlide
    ? lives.slice(start, start + itemsToShow).concat(lives.slice(0, Math.max(0, start + itemsToShow - lives.length)))
    : lives;

  const nextSlide = () => setCurrent((start + 1) % lives.length);
  const prevSlide = () => setCurrent((start - 1 + lives.length) % lives.length);

  return (
    <div className="mt-5 w-full">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-3">
        <h2 className="text-lg sm:text-xl font-semibold border-b-2 border-red-500 text-red-500">
          Watch Now & Shop Live{" "}
        </h2>
        {canSlide && (
          <div className="flex gap-3">
            <button
              onClick={prevSlide}
              className="border-2 border-green-300 px-3 py-2  bg-green-100 rounded-full"
            >
              <MdArrowBackIosNew />
            </button>
            <button
              onClick={nextSlide}
              className="border-2 border-green-300 px-3 py-2  bg-green-100 rounded-full"
            >
              <MdArrowForwardIos />
            </button>
          </div>
        )}
      </div>

      {streams === null && <p className="mt-4 text-sm text-gray-500">Loading...</p>}
      {streams?.length === 0 && <p className="mt-4 text-sm text-gray-500">No sellers are live right now.</p>}

      {lives.length > 0 && (
        <div className="relative overflow-hidden mt-4">
          <div className="flex items-stretch gap-4">
            {visibleLives.map((stream) => {
              const product = stream.user?.seller?.featuredProduct;
              const sellerName =
                stream.user?.seller?.name ||
                `${stream.user?.first_name || ""} ${stream.user?.last_name || ""}`.trim() ||
                stream.user?.username;
              // The live offer applies when bought from the live, so it is the price shown
              const salePrice = product && (product.livePrice ?? (product.discountedPrice || product.price));
              const offer = product && (product.liveOfferPercentage ?? product.offerPercentage);
              const currencySymbol = stream.user?.seller?.currency_symbol || "$";
              // Same width whether 1 or 3 sellers are live, so a single live is not stretched across the row
              return (
                <div
                  key={stream.stream_id}
                  onClick={() => navigate(liveStreamPath(stream))}
                  className="h-64 flex-shrink-0 overflow-hidden rounded-lg bg-cover bg-center bg-gray-700 relative cursor-pointer"
                  style={{
                    width: `calc((100% - ${(itemsToShow - 1) * 16}px) / ${itemsToShow})`,
                    backgroundImage: `url(${product?.thumbnailUrl || stream.user?.profile_picture_url || defaultAvatar})`,
                  }}
                >
                  {/* Top-left overlay */}
                  <span className="absolute top-2 left-2 max-w-[calc(100%-4rem)] text-white text-xs font-semibold rounded-xl">
                    <div className="flex gap-3 p-2 bg-[#D9D9D980] rounded-3xl">
                      <img
                        src={stream.user?.profile_picture_url || defaultAvatar}
                        alt=""
                        className="w-[30px] h-[30px] rounded-full bg-white object-cover"
                      />
                      <div className="text-black">
                        <p className="font-semibold text-[12px] max-w-[140px] truncate">{sellerName}</p>
                        {stream.started_at && (
                          <p className="text-[9px]">
                            {new Date(stream.started_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
                          </p>
                        )}
                      </div>
                    </div>
                    <p className="ml-2 mt-1 drop-shadow">{Number(stream.viewer_count) || 0} Viewers</p>
                  </span>
                  <span className="absolute top-2 right-2 bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    LIVE
                  </span>

                  {/* Inner product card */}
                  <div className="absolute bottom-2 right-2 w-[140px] bg-white rounded-lg shadow-lg">
                    {product ? (
                      <>
                        <div className="relative bg-[#dadadc] rounded">
                          <img
                            src={product.thumbnailUrl || "/image/shoeimg.png"}
                            alt={product.name}
                            className="w-full h-[72px] object-contain rounded-md"
                          />
                          {Number(offer) > 0 && (
                            <span className="absolute top-1 left-1 bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded">
                              {offer}% OFF
                            </span>
                          )}
                        </div>
                        <p className="px-2 pt-1 text-xs font-semibold uppercase truncate">{product.name}</p>
                        <div className="flex px-2 pb-1 justify-between items-center">
                          <div className="flex flex-col">
                            {salePrice < product.price && (
                              <span className="line-through text-gray-400 text-[10px]">{formatPrice(product.price, currencySymbol)}</span>
                            )}
                            <span className="text-black font-semibold">{formatPrice(salePrice, currencySymbol)}</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className="p-2 text-sm font-semibold line-clamp-3">{stream.title || "Live shopping"}</p>
                    )}
                    <button className="m-2 mt-0 w-[calc(100%-1rem)] rounded-full bg-[#095228] py-1 text-xs font-semibold text-white">
                      Join Live
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Dots */}
          {canSlide && (
            <div className="flex space-x-2 justify-start mt-4">
              {lives.map((stream, idx) => (
                <span
                  key={stream.stream_id}
                  className={`h-2 w-2 rounded-full focus:outline-none transition-colors ${
                    start === idx ? "bg-green-600 w-[20px]" : "bg-gray-300"
                  }`}
                ></span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
